import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import {
  FileText, Users, Settings, LayoutDashboard,
  BookOpen, LogOut, ChevronLeft, ChevronRight,
  Menu, X, Sun, Moon, Receipt, Sparkles, HelpCircle, Bot
} from 'lucide-react';
import { DPLIcon } from '@/components/brand/DPLLogo';
import { useRavanStore } from '@/stores/ravanStore';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { motion, AnimatePresence } from 'framer-motion';
import { tenantService } from '@/services/tenantService';

type NavItem = {
  label: string;
  icon: React.ElementType;
  path: string;
  external?: boolean;
};

interface AppSidebarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

const navItems: NavItem[] = [
  { label: 'Dashboard',   icon: LayoutDashboard, path: 'dashboard' },
  { label: 'Quotations',  icon: FileText,        path: 'quotations' },
  { label: 'Commercials', icon: Receipt,         path: 'invoices' },
  { label: 'Clients',     icon: Users,           path: 'clients' },
  { label: 'Accounting',  icon: BookOpen,        path: 'accounting' },
  { label: 'Settings',    icon: Settings,        path: 'settings' },
];

export function AppSidebar({ currentPath, onNavigate }: AppSidebarProps) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { toggleRavan } = useRavanStore();
  const [user, setUser] = useState<User | null>(null);
  const [tenantPlan, setTenantPlan] = useState('starter');
  const [tenantStatus, setTenantStatus] = useState('active');

  const effectiveNavItems: NavItem[] = user
    ? [
        ...navItems,
        { label: 'Guide', icon: BookOpen, path: '/guide', external: true },
        ...(tenantPlan === 'pro' || tenantPlan === 'enterprise'
          ? [{ label: 'RAVAN', icon: Sparkles, path: '/ravan', external: true }]
          : []),
      ]
    : navItems;

  useEffect(() => {
    const syncAuthContext = async (sessionUser: User | null) => {
      setUser(sessionUser);

      if (!sessionUser) {
        setTenantPlan('starter');
        setTenantStatus('inactive');
        return;
      }

      try {
        const tenant = await tenantService.getCurrentTenant();
        setTenantPlan(tenant?.plan || 'starter');
        setTenantStatus(tenant?.status || 'active');
      } catch {
        setTenantPlan('starter');
        setTenantStatus('active');
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      void syncAuthContext(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      void syncAuthContext(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setCollapsed(true);
    };
    // Set correct value on mount (safe — runs client-side only)
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && !collapsed) setCollapsed(true);
  }, [isMobile, collapsed]);

  const handleNav = (path: string) => {
    if (path === '/ravan') {
      toggleRavan();
      setMobileOpen(false);
      return;
    }
    if (path.startsWith('/')) { navigate(path); setMobileOpen(false); return; }
    onNavigate(path);
    setMobileOpen(false);
  };

  // Prevent body scroll when drawer is open on mobile
  useEffect(() => {
    if (mobileOpen && isMobile) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [mobileOpen, isMobile]);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) { toast.error('Failed to sign out'); return; }
    toast.success('Signed out');
    navigate('/login');
  };

  const initials = user?.user_metadata?.full_name
    ?.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    || user?.email?.slice(0, 2).toUpperCase() || 'U';

  const displayName = user?.user_metadata?.full_name
    || user?.email?.split('@')[0] || 'User';
  const workspaceStateLabel = `${tenantPlan.toUpperCase()} | ${tenantStatus.toUpperCase()}`;

  const NavList = ({ isCollapsed }: { isCollapsed: boolean }) => (
    <nav className="flex-1 py-4 px-3 space-y-1" data-tour="sidebar-nav">
      {effectiveNavItems.map((item) => {
        const Icon = item.icon;
        const isActive = currentPath === item.path;
        const isRavan = item.label === 'RAVAN';
        return (
          <button
            key={item.path}
            onClick={() => handleNav(item.path)}
            data-tour={item.label === 'Guide' ? 'guide-link' : undefined}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium',
              'transition-colors duration-200 group relative',
              isActive
                ? 'bg-white dark:bg-zinc-900/50 text-foreground border border-border'
                : isRavan
                  ? 'text-emerald-400 hover:text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/10 border border-emerald-500/10 hover:border-emerald-500/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-surface-hover active:bg-surface-subtle border border-transparent',
              isCollapsed && 'justify-center px-0 h-11 w-11 mx-auto'
            )}
          >
            <Icon className={cn(
              'h-4.5 w-4.5 flex-shrink-0 transition-all duration-300',
              isRavan
                ? 'text-emerald-400 group-hover:scale-110'
                : isActive
                  ? 'text-emerald-500 dark:text-emerald-400 scale-105'
                  : 'group-hover:scale-105 group-hover:text-emerald-500/80 dark:group-hover:text-emerald-400/80',
            )} />
            {!isCollapsed && (
              <span className="truncate flex items-center gap-2">
                {item.label}
                {isRavan && (
                  <span className="text-[8px] font-semibold uppercase tracking-widest
                    px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                    PRO
                  </span>
                )}
              </span>
            )}
            {isActive && (
              <div className="absolute left-0 w-1 h-5 bg-emerald-500 rounded-full blur-[2px]" />
            )}
          </button>
        );
      })}
    </nav>
  );

  return (
    <>
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 sm:h-16
        bg-white dark:bg-[#0A0A0A] border-b border-zinc-200 dark:border-white/5
        flex items-center px-3 sm:px-4 gap-2 sm:gap-3">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="h-10 w-10 sm:h-11 sm:w-11 flex items-center justify-center rounded-lg sm:rounded-xl
            bg-muted/50 text-muted-foreground hover:text-foreground transition-colors touch-target active:scale-90"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        <DPLIcon size={24} className="sm:size-[28px]" />
        <div className="flex flex-col leading-none">
          <span className="text-xs sm:text-sm font-semibold text-foreground tracking-tighter">DCode DEW</span>
          <span className="text-[9px] sm:text-[10px] text-emerald-500 font-medium uppercase tracking-widest opacity-80">Weaver</span>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-zinc-900/40 dark:bg-black/60 z-30 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileOpen && (
          <motion.aside 
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', duration: 0.15 }}
            className={cn(
              'fixed top-16 left-0 bottom-0 w-72 z-40 md:hidden',
              'bg-white dark:bg-[#0A0A0A] border-r border-zinc-200 dark:border-white/5',
              'flex flex-col overflow-y-auto',
              'safe-area-inset'
            )}
          >
            <NavList isCollapsed={false} />
            <div className="mt-auto p-3 sm:p-4 border-t border-border space-y-2">
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="w-full h-10 sm:h-11 flex items-center gap-3 px-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold uppercase
                  bg-muted/50 text-muted-foreground hover:text-foreground transition-all touch-target-h"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4 text-amber-400 shrink-0" /> : <Moon className="h-4 w-4 shrink-0" />}
                <span>Appearance</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full h-10 sm:h-11 flex items-center gap-3 px-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold uppercase
                  bg-rose-500/5 text-rose-400 hover:bg-rose-500/10 transition-all touch-target-h"
              >
                <LogOut className="h-4 w-4 shrink-0" />
                <span>Sign Out</span>
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      <motion.aside
        animate={{ width: collapsed ? 72 : 240 }}
        transition={{ type: 'tween', duration: 0.15 }}
        className={cn(
          'hidden md:flex flex-col h-screen sticky top-0 z-40 overflow-hidden',
          'bg-zinc-50 dark:bg-[#0A0A0A] border-r border-zinc-200 dark:border-white/5'
        )}
      >

        <div className={cn(
          'h-16 flex items-center shrink-0 overflow-hidden',
          collapsed ? 'justify-center' : 'px-6 gap-3'
        )}>
          <DPLIcon size={32} className="shrink-0 drop-shadow-[0_0_8px_rgba(16,185,129,0.4)]" />
          {!collapsed && (
            <div className="flex flex-col leading-none">
              <span className="text-base font-semibold text-foreground tracking-tighter">DCode DEW</span>
              <span className="text-[10px] text-emerald-500 font-medium tracking-[0.2em] uppercase opacity-70">Estimator</span>
            </div>
          )}
        </div>

        <NavList isCollapsed={collapsed} />

        <div className="shrink-0 p-3 space-y-1">
          <button
            onClick={() => {
              window.dispatchEvent(new Event('start-app-tour'));
            }}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-transparent',
              'text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors duration-200 text-sm font-medium touch-target-h',
              collapsed && 'justify-center border-border'
            )}
            title="Take Tour"
          >
            <HelpCircle className="h-4 w-4 text-emerald-500 shrink-0" />
            {!collapsed && <span className="truncate">Take Tour</span>}
          </button>

          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-transparent',
              'text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors duration-200 text-sm font-medium touch-target-h',
              collapsed && 'justify-center border-border'
            )}
            title="Toggle theme"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4 text-amber-500 shrink-0" /> : <Moon className="h-4 w-4 shrink-0" />}
            {!collapsed && <span className="truncate">Appearance</span>}
          </button>

          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-border',
              'text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors duration-200 touch-target-h',
              collapsed && 'justify-center'
            )}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            {!collapsed && <span className="text-sm font-medium">Minimize Menu</span>}
          </button>
        </div>

        <div className={cn(
          'shrink-0 p-4 border-t border-zinc-200 dark:border-white/5',
          collapsed ? 'flex flex-col items-center gap-4' : 'flex items-center gap-3'
        )}>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-600 to-amber-500 p-[1px] shadow-sm">
            <div className="w-full h-full rounded-[10px] bg-white dark:bg-zinc-950 flex items-center justify-center">
               <span className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">{initials}</span>
            </div>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground truncate leading-none mb-1">{displayName}</p>
              <div className="flex items-center gap-1.5">
                 <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                 <p className="text-[9px] font-medium text-muted-foreground tracking-wider truncate uppercase">{workspaceStateLabel}</p>
              </div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={handleLogout}
              className="w-9 h-9 flex items-center justify-center rounded-xl
                bg-rose-500/5 text-rose-500/60 hover:text-rose-400
                hover:bg-rose-500/10 transition-all shrink-0 touch-target"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </motion.aside>
    </>
  );
}
